// shared/api/progress.js
// Progress API - User progress tracking and analytics
import { supabase } from '../supabase';

/**
 * Fetch user profile with progress data
 * @param {string} userId 
 * @returns {Promise<{profile, error}>}
 */
export const fetchUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return { profile: data, error: null };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { profile: null, error };
  }
};

/**
 * Update user profile
 * @param {string} userId 
 * @param {Object} updates 
 * @returns {Promise<{profile, error}>}
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return { profile: data, error: null };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { profile: null, error };
  }
};

/**
 * Calculate and update streak
 * @param {string} userId 
 * @returns {Promise<{streak, error}>}
 */
export const updateStreak = async (userId) => {
  try {
    // Fetch user's completed sessions
    const { data: sessions, error: sessionsError } = await supabase
      .from('daily_sessions')
      .select('completed_at')
      .eq('user_id', userId)
      .not('completed_at', 'is', null)
      .order('completed_at', { ascending: false });

    if (sessionsError) throw sessionsError;

    // Get current profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('current_streak, longest_streak, last_session_date')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;

    // Calculate streak
    const today = new Date().toDateString();
    const lastSession = profile.last_session_date 
      ? new Date(profile.last_session_date).toDateString() 
      : null;
    
    let newStreak = profile.current_streak || 0;
    
    if (!lastSession) {
      // First session ever
      newStreak = 1;
    } else if (lastSession === today) {
      // Already completed today, keep streak
      newStreak = profile.current_streak || 1;
    } else {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toDateString();
      
      if (lastSession === yesterdayStr) {
        // Consecutive day, increment streak
        newStreak = (profile.current_streak || 0) + 1;
      } else {
        // Streak broken, restart
        newStreak = 1;
      }
    }

    // Update longest streak if needed
    const longestStreak = Math.max(profile.longest_streak || 0, newStreak);

    // Update profile
    const { data: updated, error: updateError } = await supabase
      .from('profiles')
      .update({
        current_streak: newStreak,
        longest_streak: longestStreak,
        last_session_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single();

    if (updateError) throw updateError;

    return { 
      streak: {
        current: newStreak,
        longest: longestStreak
      }, 
      error: null 
    };
  } catch (error) {
    console.error('Error updating streak:', error);
    return { streak: null, error };
  }
};

/**
 * Increment session count
 * @param {string} userId 
 * @returns {Promise<{success, error}>}
 */
export const incrementSessionCount = async (userId) => {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('total_sessions')
      .eq('id', userId)
      .single();

    const newCount = (profile?.total_sessions || 0) + 1;

    const { error } = await supabase
      .from('profiles')
      .update({
        total_sessions: newCount,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error incrementing session count:', error);
    return { success: false, error };
  }
};

/**
 * Add mindful minutes
 * @param {string} userId 
 * @param {number} minutes 
 * @returns {Promise<{success, error}>}
 */
export const addMindfulMinutes = async (userId, minutes) => {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('mindful_minutes')
      .eq('id', userId)
      .single();

    const newTotal = (profile?.mindful_minutes || 0) + minutes;

    const { error } = await supabase
      .from('profiles')
      .update({
        mindful_minutes: newTotal,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error adding mindful minutes:', error);
    return { success: false, error };
  }
};

/**
 * Update favorite category
 * @param {string} userId 
 * @param {string} category 
 * @returns {Promise<{success, error}>}
 */
export const updateFavoriteCategory = async (userId, category) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        favorite_category: category,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating favorite category:', error);
    return { success: false, error };
  }
};

/**
 * Get comprehensive user stats
 * @param {string} userId 
 * @returns {Promise<{stats, error}>}
 */
export const getUserStats = async (userId) => {
  try {
    // Fetch profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;

    // Fetch completed sessions
    const { data: sessions, error: sessionsError } = await supabase
      .from('daily_sessions')
      .select(`
        id,
        completed_at,
        journey_id,
        journeys (
          category
        )
      `)
      .eq('user_id', userId)
      .not('completed_at', 'is', null);

    if (sessionsError) throw sessionsError;

    // Calculate category stats
    const categoryStats = {};
    sessions?.forEach(session => {
      const category = session.journeys?.category || 'unknown';
      categoryStats[category] = (categoryStats[category] || 0) + 1;
    });

    // Get favorite category (most used)
    let favoriteCategory = null;
    let maxCount = 0;
    Object.entries(categoryStats).forEach(([cat, count]) => {
      if (count > maxCount) {
        maxCount = count;
        favoriteCategory = cat;
      }
    });

    // Calculate sessions this month
    const now = new Date();
    const thisMonthSessions = sessions?.filter(s => {
      const sessionDate = new Date(s.completed_at);
      return sessionDate.getMonth() === now.getMonth() && 
             sessionDate.getFullYear() === now.getFullYear();
    }).length || 0;

    const stats = {
      totalSessions: profile.total_sessions || 0,
      mindfulMinutes: profile.mindful_minutes || 0,
      currentStreak: profile.current_streak || 0,
      longestStreak: profile.longest_streak || 0,
      favoriteCategory: favoriteCategory || profile.favorite_category,
      categoryStats,
      thisMonthSessions,
      isPremium: profile.is_premium || false,
      achievementsCount: profile.achievements_count || 0
    };

    return { stats, error: null };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    return { stats: null, error };
  }
};

/**
 * Update user progress after session completion
 * @param {string} userId 
 * @param {Object} progressData - {category, minutesSpent}
 * @returns {Promise<{success, error}>}
 */
export const updateProgressAfterSession = async (userId, progressData = {}) => {
  try {
    // Update streak
    await updateStreak(userId);

    // Increment session count
    await incrementSessionCount(userId);

    // Add mindful minutes
    if (progressData.minutesSpent) {
      await addMindfulMinutes(userId, progressData.minutesSpent);
    }

    // Update favorite category if provided
    if (progressData.category) {
      // This will be updated based on most frequent category
      const { stats } = await getUserStats(userId);
      if (stats && stats.favoriteCategory) {
        await updateFavoriteCategory(userId, stats.favoriteCategory);
      }
    }

    console.log('✅ Progress updated for user:', userId);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating progress:', error);
    return { success: false, error };
  }
};

/**
 * Check if user has premium access
 * @param {string} userId 
 * @returns {Promise<{isPremium, error}>}
 */
export const checkPremiumStatus = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('is_premium')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return { isPremium: data?.is_premium || false, error: null };
  } catch (error) {
    console.error('Error checking premium status:', error);
    return { isPremium: false, error };
  }
};

/**
 * Upgrade user to premium
 * @param {string} userId 
 * @returns {Promise<{success, error}>}
 */
export const upgradeUserToPremium = async (userId) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        is_premium: true,
        premium_since: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
    console.log('✅ User upgraded to premium:', userId);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error upgrading to premium:', error);
    return { success: false, error };
  }
};
